import {
  leadsPartialUpdateMutation,
  leadsPipelineListInfiniteOptions,
  leadsPipelineListInfiniteQueryKey,
  leadsRetrieveOptions,
  leadsStatusesListInfiniteOptions,
  leadsStatusesListInfiniteQueryKey,
  SalesFunnelReadable
} from '@crm/api'
import { AppLink, KanbanComments } from '@crm/components'
import { CreateOpportunityModal } from '@crm/components/ChangeFieldStatus/CreateOpportunityModal'
import { RefusalReasonModal } from '@crm/components/ChangeFieldStatus/RefusalReasonModal'
import {
  ExportLeads,
  LeadKanbanCard,
  LeadsFilters
} from '@crm/features/leads/components'
import {
  useKanbanLeadsQueryParams,
  useLeadView
} from '@crm/features/leads/hooks'
import { useStyles } from '@crm/features/leads/styles'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import { Kanban, SkeletonWrapper, TitleSection } from '@shared/components'
import { useQueryParams } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import {
  OnDragEndWithCreatesOpportunity,
  OnDragEndWithRequiresRefusalReason
} from '@shared/types'
import { useQuery } from '@tanstack/react-query'
import { Button, Flex, Modal, Segmented } from 'antd'
import { Add, Import } from 'iconsax-react'
import { useState } from 'react'

import { LeadCreateData } from '../../utils'
import { LeadCreate } from '../LeadCreate'

export const LeadsKanban = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const kanbanQueryParams = useKanbanLeadsQueryParams()
  const { styles } = useStyles()
  const navigate = useAppNavigate()

  const [leadCreateData, setLeadCreateData] = useState<LeadCreateData>({
    type: undefined,
    open: false,
    leadId: undefined
  })

  const [opportunityModal, setOpportunityModal] = useState<{
    open: boolean
    leadId?: number
    onConfirm?: () => void
    rollback?: () => void
  }>({ open: false })

  const [refusalReasonModal, setRefusalReasonModal] = useState<{
    open: boolean
    leadId?: number
    onConfirm?: () => void
    rollback?: () => void
  }>({ open: false })

  const { data, isLoading } = useQuery({
    ...leadsRetrieveOptions({ path: { id: Number(leadCreateData?.leadId) } }),
    enabled: !!Number(leadCreateData?.leadId)
  })

  const { segmentedValue, segmentedOptions } = useLeadView()

  const onActionClick = (actionData: LeadCreateData) =>
    setLeadCreateData(actionData)

  const onCancelModal = () => {
    setLeadCreateData({
      open: false,
      type: undefined,
      leadId: undefined
    })
  }

  const onDragEndWithRequiresRefusalReason = ({
    kanbanId,
    statusId,
    updateStatus,
    rollback,
    fromQueryKey,
    toQueryKey
  }: OnDragEndWithRequiresRefusalReason) => {
    setRefusalReasonModal({
      open: true,
      leadId: Number(kanbanId),
      rollback,
      onConfirm: () =>
        updateStatus({ status: statusId }, [fromQueryKey, toQueryKey])
    })
  }

  const onDragEndWithCreatesOpportunity = ({
    kanbanId,
    statusId,
    updateStatus,
    rollback,
    fromQueryKey,
    toQueryKey
  }: OnDragEndWithCreatesOpportunity) => {
    setOpportunityModal({
      open: true,
      leadId: Number(kanbanId),
      rollback,
      onConfirm: () =>
        updateStatus({ status: statusId }, [fromQueryKey, toQueryKey])
    })
  }

  return (
    <>
      <TitleSection title={t(`menu.contact`)}>
        <AppLink to="/leads/import">
          <Button icon={<Import size={16} />}>{t('menu.import')}</Button>
        </AppLink>

        <ExportLeads />
      </TitleSection>

      <Flex vertical gap={16}>
        <Kanban
          header={
            <Flex justify="space-between" align="start" gap={8}>
              <Flex gap={8} wrap align="start">
                <LeadsFilters defaultLeadType />
              </Flex>

              <Flex gap={8} align="start" justify="end">
                <Button
                  icon={<Add size={16} />}
                  type="primary"
                  onClick={() => navigate(paths.leadsCreate)}
                >
                  {t('lead.add_lead')}
                </Button>

                <Segmented
                  value={segmentedValue}
                  onChange={(value) => navigate(value)}
                  options={segmentedOptions}
                />
              </Flex>
            </Flex>
          }
          cardRender={(data: SalesFunnelReadable) => (
            <LeadKanbanCard data={data} onActionClick={onActionClick} />
          )}
          statusesQuery={leadsStatusesListInfiniteOptions}
          statusesQueryKey={leadsStatusesListInfiniteQueryKey}
          pipelineListQuery={leadsPipelineListInfiniteOptions}
          pipelineListUpdateMutation={leadsPartialUpdateMutation}
          pipelineListQueryKey={leadsPipelineListInfiniteQueryKey}
          pipelineListQueryOptions={kanbanQueryParams}
          extraStatusQueryOptions={{
            lead_type: params?.lead_type
              ? [Number(params.lead_type)]
              : undefined
          }}
          onDragEndWithCreatesOpportunity={onDragEndWithCreatesOpportunity}
          onDragEndWithRequiresRefusalReason={
            onDragEndWithRequiresRefusalReason
          }
        >
          <SkeletonWrapper loading={isLoading} paragraph={{ rows: 22 }}>
            {leadCreateData?.type === 'edit' && (
              <Modal
                open
                onCancel={onCancelModal}
                width={1000}
                footer={false}
                className={styles.modal}
              >
                <LeadCreate leadDetails={data} onCancelModal={onCancelModal} />
              </Modal>
            )}
          </SkeletonWrapper>

          <Modal
            open={leadCreateData?.type === 'comments'}
            onCancel={onCancelModal}
            width={700}
            footer={false}
            className={styles.modal}
          >
            <KanbanComments
              type="lead"
              id={Number(leadCreateData?.leadId)}
              onCancel={onCancelModal}
            />
          </Modal>
        </Kanban>

        <CreateOpportunityModal
          open={opportunityModal.open}
          leadId={opportunityModal.leadId}
          onSuccess={() => {
            opportunityModal.onConfirm?.()
            setOpportunityModal({ open: false })
          }}
          onCancel={() => {
            opportunityModal.rollback?.()
            setOpportunityModal({ open: false })
          }}
        />

        <RefusalReasonModal
          open={refusalReasonModal.open}
          leadId={refusalReasonModal.leadId}
          onConfirm={() => {
            refusalReasonModal.onConfirm?.()
            setRefusalReasonModal({ open: false })
          }}
          onCancel={() => {
            refusalReasonModal.rollback?.()
            setRefusalReasonModal({ open: false })
          }}
        />
      </Flex>
    </>
  )
}
